import {DatePipe} from '@angular/common';
import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {NotificationService} from '@gatewee/services/notification.service';
import {Notification} from '@gatewee/types/notification';
import {TaskScope} from '@gatewee/types/task';
import {getNotificationHref} from '@gatewee/utils/helpers/get-notification-href';
import {TranslateService} from '@ngx-translate/core';
import {TuiAlertService} from '@taiga-ui/core';
import {catchError, of, Subject, takeUntil} from 'rxjs';

type NotificationFilter = 'unread' | 'all';

@Component({
  selector: 'gatewee-notifications',
  templateUrl: './gatewee-notifications.component.html',
  styleUrls: ['./gatewee-notifications.component.css'],
  standalone: false,
  providers: [DatePipe],
})
export class GateweeNotificationsComponent implements OnInit, OnDestroy {
  @Input({required: true}) scope!: TaskScope;
  @Input() breadcrumb: {label: string; path?: string}[] = [];
  @Input() disableContentPage = false;
  @Input() panelPrefix = '/panel';

  notifications: Notification[] = [];
  filter: NotificationFilter = 'all';
  unreadCount = 0;

  page = 1;
  pageSize = 10;
  total = 0;
  from = 0;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly notificationService: NotificationService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly alerts: TuiAlertService,
    private readonly datePipe: DatePipe,
  ) {}

  ngOnInit(): void {
    this.fetchNotifications();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get resolvedBreadcrumb(): {label: string; path?: string}[] {
    if (this.breadcrumb.length) {
      return this.breadcrumb;
    }
    return [
      {label: 'nav.breadcrumb.home', path: this.panelPrefix},
      {label: 'features.notifications.title'},
    ];
  }

  setFilter(filter: NotificationFilter): void {
    if (this.filter === filter) {
      return;
    }
    this.filter = filter;
    this.page = 1;
    this.from = 0;
    this.fetchNotifications();
  }

  onPageChange(nextPage: number): void {
    this.page = nextPage;
    this.from = (nextPage - 1) * this.pageSize;
    this.fetchNotifications();
  }

  formatDate(value: string): string {
    return this.datePipe.transform(value, 'dd MMM yyyy, HH:mm') ?? '';
  }

  get totalPages(): number {
    return Math.ceil(this.total / this.pageSize) || 1;
  }

  isUnread(notification: Notification): boolean {
    return notification.readAt === null;
  }

  isNavigable(notification: Notification): boolean {
    return getNotificationHref(notification, this.panelPrefix) !== null;
  }

  onNotificationClick(notification: Notification): void {
    const href = getNotificationHref(notification, this.panelPrefix);
    const navigate = (): void => {
      if (href) {
        void this.router.navigateByUrl(href);
      }
    };

    if (!this.isUnread(notification)) {
      navigate();
      return;
    }

    this.notificationService
      .markAsRead(this.scope, notification.id)
      .pipe(catchError(() => of(notification)))
      .subscribe(updated => {
        notification.readAt = updated.readAt;
        if (this.unreadCount > 0) {
          this.unreadCount -= 1;
        }
        if (this.filter === 'unread') {
          this.fetchNotifications();
        }
        navigate();
      });
  }

  markAllAsRead(): void {
    if (this.unreadCount === 0) {
      return;
    }

    this.notificationService
      .markAllAsRead(this.scope)
      .pipe(
        catchError(() => {
          this.alerts
            .open(
              this.translate.instant(
                'features.notifications.errors.markAllRead',
              ),
              {
                label: this.translate.instant('common.status.error'),
                appearance: 'error',
              },
            )
            .subscribe();
          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(response => {
        if (!response) {
          return;
        }
        this.unreadCount = 0;
        if (this.filter === 'unread') {
          this.notifications = [];
          this.total = 0;
          return;
        }
        this.notifications = this.notifications.map(notification => ({
          ...notification,
          readAt: notification.readAt ?? new Date().toISOString(),
        }));
      });
  }

  private fetchNotifications(): void {
    const options = this.filter === 'unread' ? {read: false} : undefined;

    this.notificationService
      .findAllPaginated(this.scope, this.from, this.pageSize, options)
      .pipe(
        catchError(() => of({records: [], count: 0, unreadCount: 0})),
        takeUntil(this.destroy$),
      )
      .subscribe(({records, count, unreadCount}) => {
        this.notifications = records;
        this.total = count;
        this.unreadCount =
          unreadCount ??
          (this.filter === 'unread'
            ? count
            : records.filter(n => n.readAt === null).length);
      });
  }
}
